import React, { useState, useRef } from 'react';
import { FileDown, ChevronDown, ChevronRight, Shield, BarChart2, Eye, Users, AlertTriangle, Activity, Map, Settings, HelpCircle, QrCode, BookOpen, CheckSquare, Loader2 } from 'lucide-react';
import { useUserStore } from '../store/userStore';

const API = process.env.REACT_APP_BACKEND_URL;

const Section = ({ id, icon: Icon, title, badge, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div id={id} className="manual-section">
      <button className="manual-section-header" onClick={() => setOpen(!open)}>
        <span className="manual-section-icon"><Icon size={18} /></span>
        <span className="manual-section-title">{title}</span>
        {badge && <span className="manual-section-badge">{badge}</span>}
        <span className="manual-section-chevron">{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
      </button>
      {open && <div className="manual-section-body">{children}</div>}
    </div>
  );
};

const Screenshot = ({ src, alt, caption }) => (
  <figure className="manual-screenshot">
    <img src={`/manual/${src}`} alt={alt} className="manual-img" />
    {caption && <figcaption className="manual-caption">{caption}</figcaption>}
  </figure>
);

const StepList = ({ steps }) => (
  <ol className="manual-steps">
    {steps.map((s, i) => (
      <li key={i}><span className="step-num">{i + 1}</span><span>{s}</span></li>
    ))}
  </ol>
);

const RoleBadge = ({ roles }) => (
  <div className="role-badges">
    {roles.map(r => (
      <span key={r} className={`role-badge role-${r.toLowerCase()}`}>{r}</span>
    ))}
  </div>
);

const InfoBox = ({ type = 'info', children }) => (
  <div className={`info-box info-${type}`}>{children}</div>
);

const TOC_ITEMS = [
  { id: 'acceso', label: '1. Acceso al sistema' },
  { id: 'dashboard', label: '2. Dashboard Principal' },
  { id: 'ops', label: '3. Módulo OPS' },
  { id: 'liderazgo', label: '4. Módulo Liderazgo' },
  { id: 'nearmiss', label: '5. Near Miss' },
  { id: 'accidentes', label: '6. Accidentes (Delt@)' },
  { id: 'siniestralidad', label: '7. Siniestralidad' },
  { id: 'miscentros', label: '8. Mis Centros (Ops)' },
  { id: 'survey123', label: '9. Registro en Survey123' },
  { id: 'admin', label: '10. Administración' },
  { id: 'exportar', label: '11. Exportaciones' },
];

export default function Ayuda() {
  const printRef = useRef();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const { token } = useUserStore();

  const handlePrint = () => window.print();

  const handleServerPdf = async () => {
    setPdfLoading(true);
    setPdfError('');
    try {
      const res = await fetch(`${API}/api/manual/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Manual_Valoriza_Safety_Hub_2026.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setPdfError('Error al generar el PDF. Prueba con "Imprimir como PDF" del navegador.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="manual-wrapper" ref={printRef}>
      {/* Header */}
      <div className="manual-header no-print-hide">
        <div className="manual-header-left">
          <BookOpen size={28} />
          <div>
            <h1 className="manual-title">Manual de Usuario</h1>
            <p className="manual-subtitle">Valoriza — Sistema de Gestión OPS &amp; Seguridad · Versión 2026</p>
          </div>
        </div>
        <div className="manual-header-actions">
          <button className="btn-pdf btn-pdf-browser" onClick={handlePrint} title="Imprimir / Guardar como PDF desde el navegador">
            <FileDown size={16} /> Imprimir PDF
          </button>
          <button className="btn-pdf" onClick={handleServerPdf} disabled={pdfLoading} title="Generar PDF con capturas reales del sistema">
            {pdfLoading ? <><Loader2 size={16} className="animate-spin" /> Generando...</> : <><FileDown size={16} /> PDF con capturas</>}
          </button>
          {pdfError && <span style={{ fontSize: 12, color: '#ef4444' }}>{pdfError}</span>}
        </div>
      </div>

      {/* Print header (only visible on print) */}
      <div className="print-only print-header">
        <div className="print-logo-row">
          <strong style={{ fontSize: 22 }}>Valoriza</strong>
          <span style={{ fontSize: 14, color: '#666' }}>Sistema de Gestión OPS &amp; Seguridad</span>
        </div>
        <h1 style={{ margin: '8px 0 4px', fontSize: 26 }}>Manual de Usuario</h1>
        <p style={{ color: '#555', fontSize: 13 }}>Versión 2026 — Confidencial</p>
        <hr style={{ margin: '12px 0' }} />
      </div>

      <div className="manual-layout">
        {/* Sidebar TOC */}
        <nav className="manual-toc no-print-hide">
          <p className="toc-title">Contenidos</p>
          {TOC_ITEMS.map(item => (
            <a key={item.id} href={`#${item.id}`} className="toc-link" onClick={e => {
              e.preventDefault();
              document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              const sec = document.getElementById(item.id)?.querySelector('.manual-section-header');
              // auto-open section
              if (sec) sec.click();
            }}>{item.label}</a>
          ))}
        </nav>

        {/* Content */}
        <main className="manual-content">

          {/* INTRO */}
          <div id="intro" className="manual-intro">
            <h2>Bienvenido al Dashboard de Seguridad y Salud</h2>
            <p>
              Esta plataforma centraliza las actividades de <strong>Observación Preventiva de Seguridad (OPS)</strong>,
              <strong> Charlas de Liderazgo</strong>, <strong>Near Miss</strong>, <strong>Accidentes</strong> y métricas de
              siniestralidad de toda la organización Valoriza, sustituyendo los reportes en Power BI.
            </p>
            <div className="roles-overview">
              <div className="role-card role-card-admin">
                <Shield size={20} /><strong>Super Admin</strong>
                <p>Acceso total. Gestión de usuarios, objetivos y sincronización.</p>
              </div>
              <div className="role-card role-card-liderazgo">
                <BarChart2 size={20} /><strong>Liderazgo</strong>
                <p>Dashboard completo con KPIs, cumplimiento y comparativas.</p>
              </div>
              <div className="role-card role-card-ops">
                <Eye size={20} /><strong>OPS / Operaciones</strong>
                <p>Vista de sus centros asignados y panel &quot;Mis Centros&quot;.</p>
              </div>
            </div>
          </div>

          {/* 1. ACCESO */}
          <Section id="acceso" icon={Shield} title="1. Acceso al sistema" defaultOpen={true}>
            <RoleBadge roles={['Admin', 'Liderazgo', 'OPS']} />
            <Screenshot src="01_login.png" alt="Pantalla de login" caption="Pantalla de acceso con opciones SSO y contraseña" />
            <h3>Opción A — Inicio de sesión con Microsoft (SSO)</h3>
            <StepList steps={[
              'Haz clic en "Inicia sesión con tu cuenta de Valoriza".',
              'Se abrirá la ventana de Microsoft Entra ID. Introduce tu email corporativo (@valorizasm.com).',
              'Si tienes MFA configurado, completa la verificación.',
              'Serás redirigido automáticamente al dashboard según tu rol.',
            ]} />
            <h3>Opción B — Acceso con contraseña</h3>
            <StepList steps={[
              'Haz clic en "Acceso con contraseña".',
              'Introduce tu email y la contraseña asignada.',
              'Haz clic en "Acceder".',
            ]} />
            <InfoBox type="warning">
              Si no puedes acceder, contacta con el administrador del sistema para que verifique
              tu usuario y los centros asignados.
            </InfoBox>
            <h3>Códigos QR de registro rápido</h3>
            <p>
              En la pantalla de login aparecen 3 códigos QR. Escaneándolos desde el móvil se abre
              directamente el formulario de Survey123 correspondiente, sin necesidad de entrar al dashboard:
            </p>
            <ul className="manual-list">
              <li><strong>OPS</strong> — Registrar una Observación Preventiva de Seguridad</li>
              <li><strong>Liderazgo</strong> — Registrar una Charla de Liderazgo</li>
              <li><strong>Near Miss</strong> — Reportar un Casi Accidente</li>
            </ul>
          </Section>

          {/* 2. DASHBOARD */}
          <Section id="dashboard" icon={BarChart2} title="2. Dashboard Principal">
            <RoleBadge roles={['Admin', 'Liderazgo']} />
            <Screenshot src="02_dashboard.png" alt="Dashboard principal" caption="Vista general del Dashboard con KPIs, cumplimiento y comparativas" />

            <h3>Filtros globales</h3>
            <p>En la barra superior puedes filtrar toda la información por:</p>
            <ul className="manual-list">
              <li><strong>Año</strong> (2024, 2025, 2026) o rango de fechas personalizado</li>
              <li><strong>Sede</strong> — una sede concreta</li>
              <li><strong>Negocio</strong> — tipo de actividad (Limpieza, Tratamiento, etc.)</li>
              <li><strong>Gerente</strong> — responsable del área</li>
              <li><strong>+ Más filtros</strong> — actividad, delegación, OPS/Charlas con o sin incumplimiento</li>
            </ul>

            <h3>Tarjetas de KPI</h3>
            <ul className="manual-list">
              <li><strong>Total OPS</strong> — Observaciones registradas en el periodo</li>
              <li><strong>Total Liderazgo</strong> — Charlas registradas</li>
              <li><strong>Near Misses</strong> — Casi accidentes reportados</li>
              <li><strong>Asistentes</strong> — Total de asistentes a charlas</li>
            </ul>

            <h3>Panel de Cumplimiento por Responsable</h3>
            <p>
              Tabla con cada gerente de zona, mostrando OPS realizadas vs. objetivo mensual,
              Liderazgo realizado vs. objetivo y número de personas formadas.
              Los porcentajes en <span style={{color:'#ef4444'}}>rojo</span> indican cumplimiento bajo (&lt;50%),
              en <span style={{color:'#f97316'}}>naranja</span> medio (50-80%) y en
              <span style={{color:'#22c55e'}}> verde</span> alto (&gt;80%).
            </p>
            <p>El icono de sobre <strong>✉️</strong> al final de cada fila envía un email de recordatorio al responsable.</p>

            <h3>Comparativa por Delegación</h3>
            <p>
              Tabla que muestra el total de OPS, Liderazgo, asistentes y Near Misses agrupados
              por delegación territorial. Útil para comparar el rendimiento entre zonas.
            </p>

            <h3>Indicadores de Centros Activos</h3>
            <p>
              Muestra cuántos centros han registrado actividad y cuántos llevan más de 30 días sin
              ningún registro (enlace directo a la lista de centros sin actividad).
            </p>
          </Section>

          {/* 3. OPS */}
          <Section id="ops" icon={Eye} title="3. Módulo OPS — Observaciones Preventivas">
            <RoleBadge roles={['Admin', 'Liderazgo', 'OPS']} />
            <Screenshot src="03_ops.png" alt="Módulo OPS" caption="Tabla de registros OPS con filtros y gráficas" />
            <p>
              Este módulo muestra todas las <strong>Observaciones Preventivas de Seguridad</strong>
              registradas en ArcGIS Survey123. Cada fila representa una visita de un encargado a un
              puesto de trabajo.
            </p>
            <h3>Columnas principales</h3>
            <ul className="manual-list">
              <li><strong>Fecha</strong> — Fecha de la observación</li>
              <li><strong>Sede</strong> — Centro donde se realizó</li>
              <li><strong>Persona</strong> — Encargado que realizó la observación</li>
              <li><strong>Actividad</strong> — Tipo de actividad observada</li>
              <li><strong>Incumplimiento</strong> — Si detectó o no una conducta de riesgo</li>
            </ul>
            <h3>Filtros disponibles</h3>
            <ul className="manual-list">
              <li>Periodo de fechas (heredado del filtro global)</li>
              <li>Sede / Centro</li>
              <li>Tipo de incumplimiento</li>
              <li>Actividad específica</li>
            </ul>
            <h3>Exportar</h3>
            <p>Botón <strong>&quot;Exportar Excel&quot;</strong> descarga todos los registros visibles con los filtros aplicados.</p>
            <InfoBox type="info">
              Los registros se sincronizan automáticamente desde ArcGIS cada 3 horas
              (00:00, 03:00, 06:00... hora de Madrid).
            </InfoBox>
          </Section>

          {/* 4. LIDERAZGO */}
          <Section id="liderazgo" icon={Users} title="4. Módulo Liderazgo — Charlas">
            <RoleBadge roles={['Admin', 'Liderazgo', 'OPS']} />
            <Screenshot src="04_liderazgo.png" alt="Módulo Liderazgo" caption="Registro de charlas y actividades de liderazgo" />
            <p>
              Muestra todas las <strong>Charlas de Liderazgo y Seguridad</strong> realizadas.
              Cada registro representa una sesión formativa con un grupo de trabajadores.
            </p>
            <h3>Columnas principales</h3>
            <ul className="manual-list">
              <li><strong>Fecha</strong> — Fecha de la charla</li>
              <li><strong>Sede</strong> — Centro donde se impartió</li>
              <li><strong>Persona</strong> — Responsable que impartió la charla</li>
              <li><strong>Actividad / Tema</strong> — Contenido de la charla</li>
              <li><strong>Asistentes</strong> — Número de trabajadores presentes</li>
              <li><strong>Incumplimiento</strong> — Si se detectó alguna situación de riesgo</li>
            </ul>
            <h3>Gráficas de evolución</h3>
            <p>El panel inferior muestra la evolución mensual de charlas y asistentes por periodo.</p>
          </Section>

          {/* 5. NEAR MISS */}
          <Section id="nearmiss" icon={AlertTriangle} title="5. Near Miss — Casi Accidentes">
            <RoleBadge roles={['Admin', 'Liderazgo']} />
            <Screenshot src="05_nearmiss.png" alt="Near Miss" caption="Registro y seguimiento de casi accidentes" />
            <p>
              El módulo de <strong>Near Miss</strong> gestiona los reportes de situaciones que
              podrían haber causado un accidente pero no lo hicieron. Es la herramienta clave para
              la prevención proactiva.
            </p>
            <h3>Información por registro</h3>
            <ul className="manual-list">
              <li>Fecha y centro donde ocurrió</li>
              <li>Descripción del incidente</li>
              <li>Tipo de riesgo (caída, atrapamiento, etc.)</li>
              <li>Estado de seguimiento</li>
              <li>Fotos adjuntas (si se aportaron)</li>
            </ul>
            <InfoBox type="warning">
              Los nuevos Near Miss generan una notificación automática al responsable del área.
              La ratio Accidentes/Near Miss es un indicador clave de la cultura preventiva.
            </InfoBox>
          </Section>

          {/* 6. ACCIDENTES */}
          <Section id="accidentes" icon={Activity} title="6. Accidentes (Delt@)">
            <RoleBadge roles={['Admin', 'Liderazgo']} />
            <Screenshot src="06_accidentes.png" alt="Módulo Accidentes" caption="Tabla de accidentes importados desde Delt@" />
            <p>
              Módulo que importa y gestiona los partes de accidentes del sistema <strong>Delt@</strong>
              (plataforma oficial de notificación de accidentes laborales del INSST).
            </p>
            <h3>Importar un CSV de Delt@</h3>
            <StepList steps={[
              'Exporta el listado de accidentes desde el portal Delt@ en formato CSV.',
              'En el módulo Accidentes, haz clic en "Importar CSV".',
              'Selecciona el archivo CSV exportado.',
              'El sistema procesará el archivo y mostrará los nuevos registros importados.',
            ]} />
            <h3>Columnas de la tabla</h3>
            <ul className="manual-list">
              <li><strong>F. Accidente</strong> — Fecha del accidente</li>
              <li><strong>F. Baja</strong> — Fecha de inicio de baja (si procede)</li>
              <li><strong>F. Alta</strong> — Fecha de alta médica (cuando el parte está cerrado)</li>
              <li><strong>Trabajador</strong> — Nombre del accidentado</li>
              <li><strong>Centro</strong> — Sede donde ocurrió</li>
              <li><strong>Modo</strong> — In itinere / En jornada</li>
              <li><strong>Gravedad</strong> — Leve / Grave / Muy grave</li>
              <li><strong>Con/Sin baja</strong> — Si generó baja laboral</li>
            </ul>
            <h3>Filtros disponibles</h3>
            <ul className="manual-list">
              <li>Tipo: Con baja / Sin baja / Todos</li>
              <li>Gravedad, modo, negocio, sede</li>
            </ul>
            <h3>Informe IA</h3>
            <p>
              El botón <strong>&quot;Generar Informe IA&quot;</strong> analiza los accidentes del periodo
              seleccionado y genera un informe ejecutivo con patrones, causas frecuentes y
              recomendaciones preventivas.
            </p>
          </Section>

          {/* 7. SINIESTRALIDAD */}
          <Section id="siniestralidad" icon={Activity} title="7. Siniestralidad">
            <RoleBadge roles={['Admin', 'Liderazgo']} />
            <Screenshot src="07_siniestralidad.png" alt="Siniestralidad" caption="Métricas de siniestralidad con índices normalizados" />
            <p>
              Panel de métricas normalizadas de siniestralidad calculadas según los estándares del INSST:
            </p>
            <ul className="manual-list">
              <li><strong>Índice de Incidencia (II)</strong> — Accidentes por cada 1.000 trabajadores</li>
              <li><strong>Índice de Frecuencia (IF)</strong> — Accidentes por millón de horas trabajadas</li>
              <li><strong>Índice de Gravedad (IG)</strong> — Jornadas perdidas por millón de horas</li>
              <li><strong>Duración Media de Baja</strong> — Días de baja por accidente</li>
            </ul>
            <h3>Costes ocultos</h3>
            <p>
              El panel calcula el coste económico estimado de los accidentes (método Heinrich),
              incluyendo costes directos e indirectos.
            </p>
          </Section>

          {/* 8. MIS CENTROS */}
          <Section id="miscentros" icon={Map} title="8. Mis Centros (solo rol OPS)">
            <RoleBadge roles={['OPS']} />
            <Screenshot src="09_mis_centros.png" alt="Panel Mis Centros" caption="Panel de semáforos de inactividad con accesos rápidos" />
            <p>
              Los usuarios con rol <strong>OPS</strong> ven al iniciar sesión un panel con todos sus
              centros asignados y su estado de actividad reciente.
            </p>
            <h3>Semáforos de inactividad</h3>
            <ul className="manual-list">
              <li><span style={{color:'#22c55e'}}>🟢 Verde</span> — El centro ha tenido actividad en los últimos 30 días</li>
              <li><span style={{color:'#f97316'}}>🟠 Naranja</span> — Sin actividad entre 30 y 60 días</li>
              <li><span style={{color:'#ef4444'}}>🔴 Rojo</span> — Sin actividad hace más de 60 días</li>
            </ul>
            <h3>Botones de acceso rápido</h3>
            <p>
              Cada centro tiene dos botones que abren la vista de OPS o Liderazgo
              <strong> pre-filtrada por ese centro</strong>, para ver directamente los registros
              sin tener que filtrar manualmente.
            </p>
          </Section>

          {/* 9. SURVEY123 */}
          <Section id="survey123" icon={QrCode} title="9. Registro de actividades en ArcGIS Survey123">
            <RoleBadge roles={['Admin', 'Liderazgo', 'OPS']} />
            <InfoBox type="info">
              Los formularios de registro <strong>se gestionan exclusivamente en ArcGIS Survey123</strong>
              (plataforma ESRI). Desde esta app solo se consultan los datos ya registrados.
              Cualquier cambio en los campos obligatorios, validaciones o flujos del formulario
              debe realizarse en el editor de Survey123.
            </InfoBox>

            <h3>Cómo registrar una OPS</h3>
            <StepList steps={[
              'Escanea el código QR de OPS de la pantalla de login, o accede a Survey123 directamente.',
              'Selecciona tu SEDE en el campo correspondiente.',
              'Indica la ACTIVIDAD que estás observando (limpieza, conducción, carga, etc.).',
              'Marca si has detectado algún INCUMPLIMIENTO (conducta de riesgo).',
              'Si hubo incumplimiento, describe brevemente la situación.',
              'Añade una foto si es posible (desde el móvil, usando la cámara).',
              'Pulsa "Enviar". El registro aparecerá en el dashboard en la próxima sincronización (máx. 3 h).',
            ]} />

            <h3>Cómo registrar una Charla de Liderazgo</h3>
            <StepList steps={[
              'Escanea el código QR de Liderazgo o accede al formulario de Liderazgo en Survey123.',
              'Selecciona la SEDE donde impartes la charla.',
              'Introduce el TEMA / ACTIVIDAD de la charla.',
              'Indica el número de ASISTENTES presentes.',
              'Marca si se detectó algún incumplimiento durante la charla.',
              'Pulsa "Enviar". El registro aparecerá en el dashboard en la próxima sincronización.',
            ]} />

            <h3>Cómo reportar un Near Miss</h3>
            <StepList steps={[
              'Escanea el código QR de Near Miss o accede al formulario en Survey123.',
              'Describe brevemente QUÉ ocurrió y DÓNDE (sede/área).',
              'Selecciona el TIPO de riesgo identificado.',
              'Añade fotos si están disponibles (muy recomendable).',
              'Pulsa "Enviar". Se generará una notificación automática al responsable del área.',
            ]} />

            <InfoBox type="warning">
              <strong>Importante:</strong> Si detectas un error en la fecha o en el centro de un registro
              ya enviado, contacta con el administrador del sistema para que aplique la corrección.
              No reenvíes el formulario con otra fecha, pues generaría un duplicado.
            </InfoBox>
          </Section>

          {/* 10. ADMIN */}
          <Section id="admin" icon={Settings} title="10. Panel de Administración">
            <RoleBadge roles={['Admin']} />
            <Screenshot src="08_admin.png" alt="Panel Administración" caption="Panel de administración con todas las configuraciones" />

            <h3>Gestión de Usuarios</h3>
            <p>
              Permite ver todos los usuarios del sistema y sus centros asignados (RLS).
              Los usuarios que acceden por SSO (Microsoft) se crean automáticamente en el primer login.
              Los usuarios con contraseña se crean desde aquí o por el administrador técnico.
            </p>

            <h3>Mapeo de Textos — Actividades</h3>
            <p>
              Los encargados escriben libremente el texto de actividad en Survey123.
              Desde aquí puedes crear <strong>alias</strong> para normalizar textos similares
              (ej.: &quot;Recogida RSU&quot;, &quot;recogida basura&quot;, &quot;RSU&quot; → se unifican como &quot;Recogida RSU&quot;).
              Esto mejora las estadísticas agrupadas por actividad.
            </p>

            <h3>Mapeo de Textos — Personas</h3>
            <p>
              Similar al anterior, permite corregir variaciones en los nombres de las personas
              que realizan OPS/Charlas (ej.: &quot;Juan García&quot;, &quot;J. García&quot;, &quot;juan garcia&quot; → &quot;JUAN GARCÍA&quot;).
            </p>

            <h3>Objetivos Mensuales por Responsable</h3>
            <p>
              Configura el número de OPS y Charlas que se espera que realice cada zona
              mensualmente. Estos objetivos se usan en el panel de Cumplimiento del Dashboard.
            </p>

            <h3>Sincronización ArcGIS</h3>
            <p>
              Muestra el estado de la última sincronización y permite lanzar una sincronización
              manual. La sincronización automática ocurre cada 3 horas.
            </p>
            <InfoBox type="info">
              Tras cualquier cambio en los alias o mapeos, pulsa <strong>&quot;Guardar y Recalcular&quot;</strong>
              para que los cambios se apliquen a todos los registros históricos.
            </InfoBox>
          </Section>

          {/* 11. EXPORTACIONES */}
          <Section id="exportar" icon={FileDown} title="11. Exportaciones">
            <RoleBadge roles={['Admin', 'Liderazgo']} />
            <h3>Exportar a Excel</h3>
            <p>
              Todos los módulos (OPS, Liderazgo, Near Miss, Accidentes) tienen un botón
              <strong> &quot;Exportar Excel&quot;</strong> que descarga los registros visibles con los
              filtros aplicados en formato .xlsx.
            </p>
            <StepList steps={[
              'Aplica los filtros deseados (sede, fecha, gerente, etc.).',
              'Haz clic en el botón "Exportar Excel" (icono de hoja de cálculo).',
              'El archivo se descarga automáticamente en tu equipo.',
            ]} />

            <h3>Exportar a PDF</h3>
            <p>
              Algunos módulos disponen de botón <strong>&quot;Exportar PDF&quot;</strong> para generar
              un informe en formato PDF listo para presentar.
            </p>

            <h3>Este Manual en PDF</h3>
            <StepList steps={[
              'Haz clic en el botón "Descargar PDF" arriba a la derecha.',
              'Tu navegador abrirá el diálogo de impresión.',
              'Selecciona "Guardar como PDF" como destino de impresión.',
              'Haz clic en "Guardar". El PDF incluirá todas las secciones expandidas.',
            ]} />
            <InfoBox type="info">
              Para obtener el mejor resultado en PDF, expande todas las secciones antes de imprimir
              y selecciona orientación <strong>Vertical</strong> con márgenes <strong>Mínimos</strong>.
            </InfoBox>
          </Section>

          {/* Footer */}
          <div className="manual-footer">
            <HelpCircle size={16} />
            <span>Para soporte técnico o dudas sobre el sistema, contacta con el equipo de Valoriza Innovation.</span>
          </div>

        </main>
      </div>

      <style>{`
        .manual-wrapper {
          min-height: 100vh;
          background: #f8fafc;
          font-family: 'Inter', system-ui, sans-serif;
          color: #1e293b;
        }
        .manual-header {
          background: #fff;
          border-bottom: 1px solid #e2e8f0;
          padding: 16px 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .manual-header-left {
          display: flex;
          align-items: center;
          gap: 14px;
          color: #16a34a;
        }
        .manual-title { margin: 0; font-size: 20px; font-weight: 700; color: #0f172a; }
        .manual-subtitle { margin: 0; font-size: 12px; color: #64748b; }
        .manual-header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .btn-pdf {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #16a34a;
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 9px 18px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }
        .btn-pdf:hover:not(:disabled) { background: #15803d; }
        .btn-pdf:disabled { opacity: 0.65; cursor: not-allowed; }
        .btn-pdf-browser { background: #0f172a; }
        .btn-pdf-browser:hover:not(:disabled) { background: #1e293b; }
        .manual-layout {
          display: flex;
          max-width: 1200px;
          margin: 0 auto;
          padding: 32px 24px;
          gap: 32px;
        }
        .manual-toc {
          width: 220px;
          flex-shrink: 0;
          position: sticky;
          top: 80px;
          align-self: flex-start;
        }
        .toc-title { font-weight: 700; font-size: 11px; text-transform: uppercase; color: #94a3b8; margin: 0 0 10px; letter-spacing: 0.08em; }
        .toc-link {
          display: block;
          font-size: 13px;
          color: #475569;
          padding: 5px 0;
          text-decoration: none;
          border-left: 2px solid transparent;
          padding-left: 10px;
          transition: all 0.15s;
        }
        .toc-link:hover { color: #16a34a; border-left-color: #16a34a; }
        .manual-content { flex: 1; min-width: 0; }
        .manual-intro {
          background: #fff;
          border-radius: 12px;
          padding: 28px;
          margin-bottom: 16px;
          border: 1px solid #e2e8f0;
        }
        .manual-intro h2 { margin: 0 0 12px; font-size: 20px; color: #0f172a; }
        .manual-intro p { margin: 0 0 20px; color: #475569; line-height: 1.6; }
        .roles-overview { display: flex; gap: 12px; flex-wrap: wrap; }
        .role-card { flex: 1; min-width: 160px; padding: 16px; border-radius: 10px; display: flex; flex-direction: column; gap: 6px; }
        .role-card strong { font-size: 15px; }
        .role-card p { margin: 0; font-size: 12px; opacity: 0.85; line-height: 1.4; }
        .role-card-admin { background: #ecfdf5; color: #166534; border: 1px solid #bbf7d0; }
        .role-card-liderazgo { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; }
        .role-card-ops { background: #fff7ed; color: #9a3412; border: 1px solid #fed7aa; }
        .manual-section {
          background: #fff;
          border-radius: 12px;
          margin-bottom: 12px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .manual-section-header {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          transition: background 0.15s;
        }
        .manual-section-header:hover { background: #f8fafc; }
        .manual-section-icon { color: #16a34a; flex-shrink: 0; }
        .manual-section-title { font-size: 16px; font-weight: 600; color: #0f172a; flex: 1; }
        .manual-section-badge { font-size: 11px; background: #f1f5f9; color: #64748b; padding: 2px 8px; border-radius: 99px; }
        .manual-section-chevron { color: #94a3b8; }
        .manual-section-body { padding: 0 24px 24px; border-top: 1px solid #f1f5f9; }
        .manual-section-body h3 { font-size: 14px; font-weight: 600; color: #374151; margin: 20px 0 8px; text-transform: uppercase; letter-spacing: 0.04em; }
        .manual-section-body p { font-size: 14px; color: #475569; line-height: 1.7; margin: 0 0 12px; }
        .manual-screenshot { margin: 16px 0 20px; }
        .manual-img { width: 100%; border-radius: 10px; border: 1px solid #e2e8f0; box-shadow: 0 2px 12px rgba(0,0,0,0.07); }
        .manual-caption { text-align: center; font-size: 12px; color: #94a3b8; margin-top: 6px; }
        .manual-steps { padding-left: 0; list-style: none; margin: 8px 0 16px; }
        .manual-steps li { display: flex; gap: 12px; font-size: 14px; color: #374151; padding: 6px 0; line-height: 1.5; }
        .step-num { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; background: #16a34a; color: #fff; border-radius: 50%; font-size: 11px; font-weight: 700; flex-shrink: 0; margin-top: 1px; }
        .manual-list { padding-left: 20px; margin: 8px 0 16px; }
        .manual-list li { font-size: 14px; color: #475569; line-height: 1.8; }
        .role-badges { display: flex; gap: 6px; flex-wrap: wrap; margin: 12px 0 16px; }
        .role-badge { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 99px; }
        .role-badge.role-admin { background: #dcfce7; color: #166534; }
        .role-badge.role-liderazgo { background: #dbeafe; color: #1e40af; }
        .role-badge.role-ops { background: #ffedd5; color: #9a3412; }
        .info-box { font-size: 13px; line-height: 1.6; padding: 12px 16px; border-radius: 8px; margin: 12px 0 16px; }
        .info-info { background: #eff6ff; color: #1e40af; border-left: 4px solid #3b82f6; }
        .info-warning { background: #fffbeb; color: #92400e; border-left: 4px solid #f59e0b; }
        .info-error { background: #fef2f2; color: #991b1b; border-left: 4px solid #ef4444; }
        .manual-footer { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #94a3b8; padding: 24px 0 8px; }
        
        /* PRINT STYLES */
        .print-only { display: none; }
        @media print {
          body { background: #fff !important; }
          .no-print-hide { display: none !important; }
          .print-only { display: block !important; }
          .manual-wrapper { background: #fff; }
          .manual-layout { display: block; padding: 0; }
          .manual-toc { display: none; }
          .manual-section { border: 1px solid #e2e8f0; page-break-inside: avoid; margin-bottom: 16px; break-inside: avoid; }
          .manual-section-body { display: block !important; }
          .manual-section-header { background: #f8fafc !important; }
          .manual-section-chevron { display: none; }
          .btn-pdf { display: none; }
          .manual-intro { border: 1px solid #e2e8f0; padding: 16px; }
          .manual-img { max-height: 350px; object-fit: contain; }
          .print-header { display: block !important; padding: 0 0 16px; }
          .print-logo-row { display: flex; justify-content: space-between; align-items: center; }
        }
      `}</style>
    </div>
  );
}
